/**
 * Pure arithmetic for the clearance chart (`ClearanceChart.tsx`).
 *
 * It lives here, next to the other clearance lib modules, for the same reason
 * `stationVolumes/chartLayout.ts` does: the grouping and the tick maths are
 * assertable on their own, and a React test should not be the only thing
 * standing between a corridor-length run and 4 000 one-pixel `<rect>`s.
 */
import { SV_COLORS, type SvPalette } from '@/lib/stationVolumes/tokens';

import type { ClearanceBin, ClearanceNearMiss } from './types';

/** `var(--sv-x, <the light palette's x>)` — see `CLEARANCE_FILL`. */
const swatch = (key: keyof SvPalette): string => `var(--sv-${key}, ${SV_COLORS.light[key]})`;

export type ClearanceMetric = 'count' | 'depth';

/**
 * One drawn bar. `fromBin`/`toBin` are SOURCE bin indices, so a click on a
 * grouped bar brushes the whole range it stands for rather than one arbitrary
 * member — the same contract `SuperBin` gives the station chart.
 */
export interface ClearanceSuperBin {
  i: number;
  fromBin: number;
  toBin: number;
  /**
   * The group's INTERNAL station span (ruling P6). The display stations below
   * are what the axis prints; these are what the alignment, the walk cursor
   * and `clipBoxAtStation` understand. A click on a bar walks to
   * `binCentreStation(b)`, so the super-bin has to carry them — converting a
   * display station back through the run's equations inside a click handler
   * would be the same arithmetic done twice, in the wrong place.
   */
  staStartInternalM: number;
  staEndInternalM: number;
  staStartDisplayM: number;
  staEndDisplayM: number;
  pointCount: number;
  violationCount: number;
  /** null only when NO member bin had a violation. */
  maxIntrusionM: number | null;
  nearMiss: ClearanceNearMiss;
}

const asSuper = (b: ClearanceBin, i: number): ClearanceSuperBin => ({
  i,
  fromBin: b.i,
  toBin: b.i,
  staStartInternalM: b.staStartInternalM,
  staEndInternalM: b.staEndInternalM,
  staStartDisplayM: b.staStartDisplayM,
  staEndDisplayM: b.staEndDisplayM,
  pointCount: b.pointCount,
  violationCount: b.violationCount,
  maxIntrusionM: b.maxIntrusionM,
  nearMiss: b.nearMiss,
});

/**
 * Aggregate `bins` down to at most `maxBars` bars.
 *
 * Counts SUM (exact — a violation counted in two adjacent bins is two
 * violations) and the intrusion depth takes the MAX, never a mean: the deepest
 * intrusion in a 200 m stretch is the fact an engineer walks out to, and
 * averaging it away is how a chart hides the one bin that matters. Near-miss
 * counts sum like the violations; they stay nested afterwards because each
 * member's ladder was already nested.
 */
export function groupClearanceBins(
  bins: readonly ClearanceBin[],
  maxBars: number,
): { bins: ClearanceSuperBin[]; groupSize: number } {
  const n = bins.length;
  const cap = Math.max(1, Math.floor(maxBars));
  if (n === 0) return { bins: [], groupSize: 1 };
  if (n <= cap) return { bins: bins.map(asSuper), groupSize: 1 };

  const groupSize = Math.ceil(n / cap);
  const out: ClearanceSuperBin[] = [];
  for (let start = 0, k = 0; start < n; start += groupSize, k++) {
    const part = bins.slice(start, Math.min(start + groupSize, n));
    const last = part[part.length - 1];
    let violationCount = 0;
    let pointCount = 0;
    let worst: number | null = null;
    const nearMiss: ClearanceNearMiss = { within0_1M: 0, within0_2M: 0, within0_35M: 0 };
    for (const b of part) {
      violationCount += b.violationCount;
      pointCount += b.pointCount;
      if (b.maxIntrusionM !== null && (worst === null || b.maxIntrusionM > worst)) worst = b.maxIntrusionM;
      nearMiss.within0_1M += b.nearMiss.within0_1M;
      nearMiss.within0_2M += b.nearMiss.within0_2M;
      nearMiss.within0_35M += b.nearMiss.within0_35M;
    }
    out.push({
      i: k,
      fromBin: part[0].i,
      toBin: last.i,
      staStartInternalM: part[0].staStartInternalM,
      staEndInternalM: last.staEndInternalM,
      staStartDisplayM: part[0].staStartDisplayM,
      staEndDisplayM: last.staEndDisplayM,
      pointCount,
      violationCount,
      maxIntrusionM: worst,
      nearMiss,
    });
  }
  return { bins: out, groupSize };
}

/** The plotted value of a bar under the active metric. */
export const metricValue = (b: ClearanceSuperBin, metric: ClearanceMetric): number =>
  metric === 'count' ? b.violationCount : (b.maxIntrusionM ?? 0);

/**
 * `Math.max(...xs)` spreads its argument list onto the stack and throws
 * `RangeError` somewhere north of ~100 k arguments. A corridor-length run at a
 * 1 m interval is well inside that, but the reduce costs nothing and removes
 * the cliff entirely.
 */
export function metricMax(bins: readonly ClearanceSuperBin[], metric: ClearanceMetric): number {
  let peak = 0;
  for (const b of bins) peak = Math.max(peak, metricValue(b, metric));
  // Never zero: it is the divisor for every bar height.
  return peak > 0 ? peak : 1;
}

/**
 * Whether a bar carries any signal at all, and of which kind. Drives both the
 * fill and the minimum drawn height — ruling K2's near-miss colouring is only
 * a colouring if the shape it colours has a non-zero height, and a near-miss
 * bin plots zero under BOTH metrics (no violations to count, no intrusion
 * depth to measure).
 */
export type ClearanceBarKind = 'violation' | 'nearMiss' | 'clear';

/**
 * The K2 ladder, as ONE predicate (ruling P14). Structural on purpose: a
 * `ClearanceSuperBin`, a `ClearanceBin` and anything else carrying a violation
 * count and the near-miss ladder all satisfy it, so the chart's bars, the 3D
 * corridor plates (`clearanceBandState`) and both legends escalate on exactly
 * the same evidence. Duplicating it once was already enough to put a bin amber
 * in the scene and neutral in the chart.
 *
 * The near-miss trigger is the OUTERMOST band (`within0_35M`), not the
 * tightest: the ARTC contingency ladder is nested, so `within0_35M` is "a point
 * came within the ladder at all", and that conservative read is the one a
 * clearance sweep exists to surface. The consequence is intended and visible —
 * more amber bars than before.
 */
export const k2Verdict = (b: {
  violationCount: number;
  nearMiss: Pick<ClearanceNearMiss, 'within0_35M'>;
}): ClearanceBarKind => (b.violationCount > 0 ? 'violation' : b.nearMiss.within0_35M > 0 ? 'nearMiss' : 'clear');

/** The chart's name for `k2Verdict`. Not a wrapper — the same function. */
export const barKind: (b: ClearanceSuperBin) => ClearanceBarKind = k2Verdict;

/**
 * The K2 ladder's ONE pairing with the palette (`SvPalette`): the chart's bars
 * and legend read it as CSS custom properties, `clearanceBandColor` reads the
 * very same palette fields as hex for the 3D envelope's per-bin bands. Two
 * renderers, one map — so a light/dark flip repaints both, and no hex is
 * forked.
 *
 * The custom property for a palette field is `--sv-<field>` by construction
 * (`tokens.ts`'s `vars`), which is what lets `CLEARANCE_FILL` below be derived
 * rather than restated.
 */
export const CLEARANCE_K2_KEY = {
  violation: 'cut',
  // `caution`, not `warn` (findings M3/M4). `warn` is documented in `tokens.ts`
  // as a STATUS ink and is spent, in this dock's very own header row, on the
  // "alignment changed" chip — one hue cannot mean both "close to the limit"
  // and "possibly the wrong stations". `caution` is the CLEARANCE ramp's own
  // boundary amber, so the near-miss bar, the near-miss plate and the band of
  // the cloud they describe are ONE colour instead of three adjacent ambers.
  nearMiss: 'caution',
  clear: 'clear',
} as const satisfies Record<ClearanceBarKind, keyof SvPalette>;

/**
 * Ruling K2, drawn: a bar is red once anything actually intruded, amber when
 * nothing did but a point sat inside the tightest near-miss band, and neutral
 * otherwise. Three states rather than two, because "clear" and "cleared it by
 * 6 cm" are the two facts a clearance sweep exists to separate.
 *
 * The heights that make the amber visible live in `BAR_FLOOR_PX` below: a
 * near-miss bin plots ZERO under both metrics, so without a floor the colour
 * would be applied to a rect of zero height and never render.
 *
 * The literal fallback is the LIGHT palette's own value rather than a
 * hand-picked rgb() — the fallback only ever applies outside `.sv-root`, where
 * any value is a guess, and a guess that came from the palette cannot drift
 * from it.
 */
export const CLEARANCE_FILL = {
  violation: swatch(CLEARANCE_K2_KEY.violation),
  nearMiss: swatch(CLEARANCE_K2_KEY.nearMiss),
  clear: swatch(CLEARANCE_K2_KEY.clear),
} as const satisfies Record<ClearanceBarKind, string>;

/**
 * The K2 key's labels. Named as well as coloured: red and amber at 10 px are
 * not an identity for a reader who cannot separate the two hues. Shared with
 * the 3D bands' legend in `ClearanceDock`, so the two keys cannot disagree
 * about what the colours mean.
 */
export const CLEARANCE_LEGEND_KEYS = {
  violation: 'clearance.chart.legendViolation',
  nearMiss: 'clearance.chart.legendNearMiss',
  clear: 'clearance.chart.legendClear',
} as const satisfies Record<ClearanceBarKind, string>;

/** The order a bar escalates through the ladder — the order the key is read in. */
export const CLEARANCE_LEGEND_ORDER: ClearanceBarKind[] = ['violation', 'nearMiss', 'clear'];

/** Minimum drawn height in px per kind — see `barKind`. */
export const BAR_FLOOR_PX: Record<ClearanceBarKind, number> = {
  // A violating bin always has a real value to scale, so it needs only enough
  // to survive rounding at the bottom of the range.
  violation: 1,
  // The whole point of H2: a near-miss stub has to be tall enough to read as
  // amber, because its plotted value is zero in both metrics.
  nearMiss: 3,
  // A clean run's Chart tab should still read as "measured, and clear" rather
  // than as an empty frame, so a clean bin gets a hairline of its own.
  clear: 1,
};

/** Drawn bar height: the scaled value, floored by kind. */
export function barHeightPx(b: ClearanceSuperBin, metric: ClearanceMetric, maxValue: number, innerH: number): number {
  const scaled = (metricValue(b, metric) / maxValue) * innerH;
  return Math.max(BAR_FLOOR_PX[barKind(b)], scaled);
}

/**
 * Round tick values from 0 to `maxValue`, at most `count` of them.
 * `integersOnly` (the count metric) never emits `3.5 violations`.
 */
export function axisTicks(maxValue: number, count: number, integersOnly: boolean): number[] {
  if (!(maxValue > 0) || count < 2) return [0];
  const raw = maxValue / (count - 1);
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  // Nearest nice step, not the next one UP: rounding up turns a max of 7 into a
  // single `5` gridline and an axis with two labels on it, which is worse than
  // no axis. The 1.5 divisor is what makes the choice "nearest on a log scale".
  const step0 = [1, 2, 5, 10].map((m) => m * mag).find((s) => s >= raw / 1.5) ?? 10 * mag;
  const step = integersOnly ? Math.max(1, Math.round(step0)) : step0;
  const out: number[] = [];
  for (let v = 0; v <= maxValue + step * 1e-6; v += step) out.push(Math.round(v * 1e6) / 1e6);
  return out;
}
